import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, Circle } from 'lucide-react';
import type React from 'react';

import { cn } from '~/utils/cn.ts';

const DropdownMenu = DropdownMenuPrimitive.Root;
const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;
const DropdownMenuGroup = DropdownMenuPrimitive.Group;
const DropdownMenuPortal = DropdownMenuPrimitive.Portal;
const DropdownMenuSub = DropdownMenuPrimitive.Sub;
const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

function DropdownMenuSubTrigger({
	className,
	inset,
	children,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
	inset?: boolean;
}) {
	return (
		<DropdownMenuPrimitive.SubTrigger
			className={cn(
				'flex cursor-default select-none items-center rounded-none',
				'px-2 py-1.5 font-medium text-xs outline-hidden',
				'focus:bg-accent focus:text-accent-foreground',
				'data-[state=open]:bg-accent',
				inset && 'pl-8',
				className,
			)}
			data-slot="dropdown-menu-subtrigger"
			{...props}
		>
			{children}
			<ChevronRight className="ml-auto size-4" />
		</DropdownMenuPrimitive.SubTrigger>
	);
}

function DropdownMenuSubContent({
	className,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
	return (
		<DropdownMenuPrimitive.SubContent
			className={cn(
				'z-[calc(var(--z-index-dialog)+1)] min-w-[8rem] overflow-hidden rounded-none p-1 shadow-md',
				'data-[state=closed]:animate-out data-[state=open]:animate-in',
				'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
				'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
				'slide-in-from-left-1',
				'text-popover-foreground',
				'bg-popover',
				'border border-border',
				className,
			)}
			data-slot="dropdown-menu-subcontent"
			{...props}
		/>
	);
}

function DropdownMenuContent({
	className,
	sideOffset = 4,
	container,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content> & {
	container?: HTMLElement | null | undefined;
}) {
	return (
		<DropdownMenuPrimitive.Portal container={container}>
			<DropdownMenuPrimitive.Content
				className={cn(
					'z-[calc(var(--z-index-dialog)+1)] min-w-[8rem] overflow-hidden rounded-none p-1 shadow-md',
					'data-[state=closed]:animate-out data-[state=open]:animate-in',
					'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
					'data-[state=open]:zoom-in-95 data-[state=closed]:zoom-out-95',
					'data-[side=right]:slide-in-from-left-2',
					'data-[side=left]:slide-in-from-right-2',
					'data-[side=bottom]:slide-in-from-top-2',
					'data-[side=top]:slide-in-from-bottom-2',
					'border border-border',
					'bg-popover',
					'text-popover-foreground',
					className,
				)}
				data-slot="dropdown-menu-content"
				sideOffset={sideOffset}
				{...props}
			/>
		</DropdownMenuPrimitive.Portal>
	);
}

function DropdownMenuItem({
	className,
	inset,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
	inset?: boolean;
}) {
	return (
		<DropdownMenuPrimitive.Item
			className={cn(
				'relative flex cursor-default select-none items-center px-2 py-1.5',
				'rounded-none font-medium text-xs outline-hidden',
				'data-disabled:pointer-events-none data-disabled:opacity-50',
				'focus:bg-accent focus:text-accent-foreground',
				inset && 'pl-8',
				className,
			)}
			data-slot="dropdown-menu-item"
			{...props}
		/>
	);
}

function DropdownMenuCheckboxItem({
	className,
	children,
	checked,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
	return (
		<DropdownMenuPrimitive.CheckboxItem
			checked={checked}
			className={cn(
				'relative flex cursor-default select-none items-center rounded-none',
				'py-1.5 pr-2 pl-8 font-medium text-xs outline-hidden',
				'focus:bg-accent focus:text-accent-foreground',
				'data-disabled:pointer-events-none data-disabled:opacity-50',
				className,
			)}
			data-slot="dropdown-menu-checkbox-item"
			{...props}
		>
			<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
				<DropdownMenuPrimitive.ItemIndicator>
					<Check className="size-4" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
			{children}
		</DropdownMenuPrimitive.CheckboxItem>
	);
}

function DropdownMenuRadioItem({
	className,
	children,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
	return (
		<DropdownMenuPrimitive.RadioItem
			className={cn(
				'relative flex cursor-default select-none items-center rounded-none',
				'py-1.5 pr-2 pl-8 font-medium text-xs outline-hidden',
				'focus:bg-accent focus:text-accent-foreground',
				'data-disabled:pointer-events-none data-disabled:opacity-50',
				className,
			)}
			data-slot="dropdown-menu-radio-item"
			{...props}
		>
			<span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
				<DropdownMenuPrimitive.ItemIndicator>
					<Circle className="h-2 w-2 fill-current" />
				</DropdownMenuPrimitive.ItemIndicator>
			</span>
			{children}
		</DropdownMenuPrimitive.RadioItem>
	);
}

function DropdownMenuLabel({
	className,
	inset,
	...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
	inset?: boolean;
}) {
	return (
		<DropdownMenuPrimitive.Label
			className={cn('px-2 py-1.5 font-semibold text-foreground text-xs', inset && 'pl-8', className)}
			data-slot="dropdown-menu-label"
			{...props}
		/>
	);
}

function DropdownMenuSeparator({ className, ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
	return (
		<DropdownMenuPrimitive.Separator
			className={cn('-mx-1 my-1 h-px bg-border', className)}
			data-slot="dropdown-menu-separator"
			{...props}
		/>
	);
}

function DropdownMenuShortcut({ className, ...props }: React.HTMLAttributes<HTMLSpanElement>) {
	return (
		<span
			className={cn('ml-auto text-muted-foreground text-xs tracking-widest', className)}
			data-slot="dropdown-menu-shortcut"
			{...props}
		/>
	);
}

export {
	DropdownMenu,
	DropdownMenuCheckboxItem,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuPortal,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuShortcut,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
};
